import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Container } from 'react-bootstrap';
// @ts-ignore
import styles from './PriceChange.module.scss?module';
import PriceChangeItem from './PriceChangeItem/PriceChangeItem';
import {
  groupPriceChanges,
  PriceChangeFilter,
  PriceChangeGroup,
  PriceChangeRow,
} from './priceChangeGroups';
import { CustomerDashboardPayload } from '../../Index/CustomerDashboard/types';
import LoadingSpinner from '../../../Abstracts/LoadingSpinner/LoadingSpinner';
import { ProductListProvider } from '../../../Context/ProductListContext';
import { useUser } from '../../../Context/UserContext';
import { getRequest, postRequest } from '../../../Helper/ApiClient';

const EMPTY_STATES: Record<PriceChangeFilter, string> = {
  all: 'No price changes right now',
  stocked: 'No price changes on items you stock',
  down: 'No price decreases right now',
};

/* The page behind "See all price changes" (Marketing 04.11.2026, P-5094):
   all price changes, the day as subheading, one row per product as in the
   dashboard's "Info overview", the row opens the quick order block below it.
   It serves dealers who re-price their shelf or look for a cheaper buy.

   The sizes the customer stocks (bought in the last 12 months) come from the
   dashboard payload: they lead each day, carry "In your range" and have
   their own filter.
   Customers without the dashboard (dashboard_enabled) get the plain list;
   the request is not sent for them. */
export default function PriceChange() {
  const { user } = useUser();
  const dashboardEnabled = !!user?.dashboard_enabled;

  // Same paths as the dashboard, so react-query shares the cache with it.
  const priceChangeRequest = getRequest<PriceChangeRow[]>(
    '/api/onlineshop/price-change-products'
  );
  const dashboardRequest = getRequest<CustomerDashboardPayload>(
    '/api/onlineshop/customer-dashboard',
    undefined,
    { enabled: dashboardEnabled }
  );
  const visitPageMutation = postRequest('/api/onlineshop/visit-page');

  const [filter, setFilter] = useState<PriceChangeFilter>('all');
  // One open row at a time, so the list stays a list.
  const [openId, setOpenId] = useState<number | null>(null);

  useEffect(() => {
    const formData = new FormData();
    formData.append('page', 'price_change');
    visitPageMutation.mutate(formData);
  }, []);

  // A dead session can answer with the login page's HTML instead of JSON.
  const rows = Array.isArray(priceChangeRequest.data)
    ? priceChangeRequest.data
    : [];
  const stocked = useMemo(
    () =>
      new Set<number>(
        dashboardRequest.data?.stocked_product_size_erp_ids ?? []
      ),
    [dashboardRequest.data]
  );

  // The rows of every filter, so each chip counts what it would show.
  const groupsByFilter = useMemo(
    () => ({
      all: groupPriceChanges(rows, 'all', stocked),
      stocked: groupPriceChanges(rows, 'stocked', stocked),
      down: groupPriceChanges(rows, 'down', stocked),
    }),
    [rows, stocked]
  );
  const countRows = (groups: PriceChangeGroup[]) =>
    groups.reduce((sum, group) => sum + group.rows.length, 0);
  const counts: Record<PriceChangeFilter, number> = {
    all: countRows(groupsByFilter.all),
    stocked: countRows(groupsByFilter.stocked),
    down: countRows(groupsByFilter.down),
  };
  const groups = groupsByFilter[filter];

  const chips: { key: PriceChangeFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    // Only with something to show: an empty "Your items" chip is noise.
    ...(counts.stocked > 0
      ? [{ key: 'stocked' as const, label: 'Your items' }]
      : []),
    ...(counts.down > 0 ? [{ key: 'down' as const, label: 'Price down' }] : []),
  ];

  const selectFilter = (key: PriceChangeFilter) => {
    setFilter(key);
    setOpenId(null);
  };

  return (
    <ProductListProvider>
      <Container className={styles.container}>
        <div className={styles.page}>
          <h1 className={styles.title}>Price changes</h1>
          {priceChangeRequest.isPending ? (
            <LoadingSpinner size="sm" />
          ) : priceChangeRequest.isError ? (
            <p className={styles.emptyState} role="alert">
              Price changes could not be loaded. Please try again later.
            </p>
          ) : (
            <>
              {rows.length > 0 && chips.length > 1 && (
                <div
                  className={styles.chips}
                  role="group"
                  aria-label="Filter price changes"
                >
                  {chips.map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      className={clsx(
                        styles.chip,
                        chip.key === filter && styles.chipActive
                      )}
                      aria-pressed={chip.key === filter}
                      onClick={() => selectFilter(chip.key)}
                    >
                      {chip.label}
                      <span className={styles.chipCount}>
                        {counts[chip.key]}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {groups.length === 0 ? (
                <p className={styles.emptyState}>{EMPTY_STATES[filter]}</p>
              ) : (
                groups.map((group) => (
                  <section key={group.date} className={styles.group}>
                    <h2 className={styles.groupDate}>
                      {group.isoDate ? (
                        <time dateTime={group.isoDate}>{group.date}</time>
                      ) : (
                        group.date
                      )}
                      <span className={styles.groupCount}>
                        {` · ${group.rows.length} ${
                          group.rows.length === 1 ? 'item' : 'items'
                        }`}
                      </span>
                    </h2>
                    <ul className={styles.list}>
                      {group.rows.map((row) => (
                        <PriceChangeItem
                          key={row.id}
                          row={row}
                          stocked={
                            filter !== 'stocked' &&
                            stocked.has(row.product_size_erp_id)
                          }
                          open={openId === row.id}
                          onToggle={() =>
                            setOpenId(openId === row.id ? null : row.id)
                          }
                        />
                      ))}
                    </ul>
                  </section>
                ))
              )}
            </>
          )}
        </div>
      </Container>
    </ProductListProvider>
  );
}
